import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import '../styles/design-system.css';

const FloatingWhatsApp = () => {
  const { contactData } = useLanguage();
  const [showTooltip, setShowTooltip] = useState(false);
  const [visible, setVisible] = useState(false);
  const whatsappNumber = contactData.whatsapp.replace(/\D/g, '');
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=Olá! Gostaria de mais informações sobre os serviços da Lucimar Bizoni.`;

  // Show button after scrolling a bit
  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > 200);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-show tooltip after 4s then hide
  useEffect(() => {
    if (!visible) return;
    const t1 = setTimeout(() => setShowTooltip(true), 4000);
    const t2 = setTimeout(() => setShowTooltip(false), 8000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed bottom-6 right-4 sm:right-6 z-50 flex items-center gap-3"
          initial={{ opacity: 0, scale: 0.5, x: 40 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          exit={{ opacity: 0, scale: 0.5, x: 40 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        >
          {/* Tooltip bubble */}
          <AnimatePresence>
            {showTooltip && (
              <motion.div
                initial={{ opacity: 0, x: 12, scale: 0.9 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 12, scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: '0.75rem 1rem',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                  fontSize: '0.8rem',
                  fontFamily: 'Inter, sans-serif',
                  color: '#333',
                  maxWidth: 200,
                  lineHeight: 1.4,
                  whiteSpace: 'nowrap',
                  position: 'relative',
                }}
              >
                Fale comigo no WhatsApp! 👋
                {/* Arrow */}
                <div style={{
                  position: 'absolute',
                  right: -8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  borderLeft: '8px solid #fff',
                  borderTop: '6px solid transparent',
                  borderBottom: '6px solid transparent',
                }} />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Button */}
          <div style={{ position: 'relative' }}>
            {/* Pulse rings */}
            {[1, 2].map((i) => (
              <motion.div
                key={i}
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  background: '#25D366',
                }}
                animate={{
                  scale: [1, 1.6 + i * 0.3],
                  opacity: [0.5, 0],
                }}
                transition={{
                  duration: 2,
                  delay: i * 0.5,
                  repeat: Infinity,
                  ease: 'easeOut',
                }}
              />
            ))}

            <motion.a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Falar no WhatsApp"
              style={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: '#25D366',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 20px rgba(37, 211, 102, 0.4)',
                position: 'relative',
                zIndex: 1,
                textDecoration: 'none',
              }}
              whileHover={{ scale: 1.12, boxShadow: '0 8px 30px rgba(37, 211, 102, 0.5)' }}
              whileTap={{ scale: 0.93 }}
              onHoverStart={() => setShowTooltip(true)}
              onHoverEnd={() => setShowTooltip(false)}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            >
              <MessageCircle size={28} color="white" />
            </motion.a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FloatingWhatsApp;
